import { useCallback, useContext, useMemo, useRef, useState } from 'react'

import { type SanityCombinedListingProductFragment } from '@data/sanity/queries/types/product'
import {
  type CombinedListingProductType,
  getCombinedListingProductDefaultValues,
  getCombinedListingProductDropdownOptions,
  getCombinedListingProductsBySize,
  ProductCombinedListingContext,
} from '@lib/product/combined-listing'
import { getProductGalleryPhoto } from '@lib/product/images'
import { type DropdownOption, getOptionTitle } from '@lib/product/option'
import { getSizeDimensionsFromString } from '@lib/size'
import { StringsContext } from '@lib/strings-context'

import Accordion from '@components/accordion'
import Button from '@components/buttons/button'
import Link from '@components/link'
import RangeSlider from '@components/range-slider'
import SimplePortableText from '@components/simple-portable-text'
import OptionListItem from './combined-listing-option-list-item'
import OptionThumbnail from './combined-listing-option-thumbnail'
import SmallGraphic from './small-graphic'

interface CombinedListingProductSelectProps {
  onMainProductChange: (product: SanityCombinedListingProductFragment) => void
  frameColor?: string
  accordionOpen?: string
  onAccordionToggle?: (value: string) => void
}

const PASSEPARTOUT_MIN_VALUE = 1

const CombinedListingProductSelect = ({
  accordionOpen,
  onAccordionToggle,
  onMainProductChange,
  frameColor,
}: CombinedListingProductSelectProps) => {
  const strings = useContext(StringsContext)
  const {
    combinedListingSettings,
    combinedListingConfiguration,
    combinedListingMainProductId,
    combinedListingActiveProducts,
    combinedListingActiveSize,
    combinedListingActivePassepartoutHoleSize,
    addCombinedListingProduct,
    clearCombinedListingProductType,
    setCombinedListingSize,
    setCombinedListingPassepartoutHoleSize,
  } = useContext(ProductCombinedListingContext)

  const [passepartoutHoleWidth, setPassepartoutHoleWidth] = useState<
    number | null
  >(null)
  const [passepartoutHoleHeight, setPassepartoutHoleHeight] = useState<
    number | null
  >(null)

  const isDefaultSelectionLoadedRef = useRef(false)

  const sizeDimensions = useMemo(
    () => getSizeDimensionsFromString(combinedListingActiveSize ?? ''),
    [combinedListingActiveSize],
  )

  const passepartoutHoleSizeDimensions = useMemo(
    () =>
      getSizeDimensionsFromString(
        combinedListingActivePassepartoutHoleSize ?? '',
      ),
    [combinedListingActivePassepartoutHoleSize],
  )

  // Product IDs for dropdown input values
  const glassId = useMemo(
    () =>
      combinedListingActiveProducts?.find(
        (product) => product.combinedListingProductType === 'glass',
      )?.productID,
    [combinedListingActiveProducts],
  )
  const backboardId = useMemo(
    () =>
      combinedListingActiveProducts?.find(
        (product) => product.combinedListingProductType === 'backboard',
      )?.productID,
    [combinedListingActiveProducts],
  )
  const passepartoutId = useMemo(
    () =>
      combinedListingActiveProducts?.find(
        (product) => product.combinedListingProductType === 'passepartout',
      )?.productID,
    [combinedListingActiveProducts],
  )
  const spacerId = useMemo(
    () =>
      combinedListingActiveProducts?.find(
        (product) => product.combinedListingProductType === 'spacer',
      )?.productID,
    [combinedListingActiveProducts],
  )

  // Options for dropdown inputs
  const frameOptions = useMemo(
    () =>
      getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.frame ?? [],
        null,
      ),
    [combinedListingConfiguration],
  )
  const sizeOptions = useMemo<DropdownOption[]>(() => {
    const combinedListing = getCombinedListingProductsBySize(
      combinedListingConfiguration?.frame ?? [],
      combinedListingConfiguration?.glass ?? [],
      combinedListingConfiguration?.backboard ?? [],
      combinedListingConfiguration?.passepartout ?? [],
      combinedListingConfiguration?.spacer ?? [],
    )

    return Object.keys(combinedListing).map((size) => ({
      value: size,
      title: size,
    }))
  }, [combinedListingConfiguration])
  const glassOptions = useMemo(
    () =>
      getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.glass ?? [],
        combinedListingActiveSize ?? null,
        strings.combinedListingNoGlass,
        strings.combinedListingNoGlassDescription,
      ),
    [combinedListingActiveSize, combinedListingConfiguration, strings],
  )

  const backboardOptions = useMemo(
    () =>
      getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.backboard ?? [],
        combinedListingActiveSize ?? null,
        strings.combinedListingNoBackboard,
        strings.combinedListingNoBackboardDescription,
      ),
    [combinedListingActiveSize, combinedListingConfiguration, strings],
  )
  const passepartoutOptions = useMemo(
    () =>
      getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.passepartout ?? [],
        combinedListingActiveSize ?? null,
        strings.combinedListingNoPassepartout,
        strings.combinedListingNoPassepartoutDescription,
      ),
    [combinedListingActiveSize, combinedListingConfiguration, strings],
  )
  const spacerOptions = useMemo(
    () =>
      getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.spacer ?? [],
        combinedListingActiveSize ?? null,
        strings.combinedListingNoSpacer,
        strings.combinedListingNoSpacerDescription,
      ),
    [combinedListingActiveSize, combinedListingConfiguration, strings],
  )
  const hasGlassOptions = useMemo(
    () => glassOptions.some((option) => !!option.value),
    [glassOptions],
  )
  const hasBackboardOptions = useMemo(
    () => backboardOptions.some((option) => !!option.value),
    [backboardOptions],
  )
  const hasPassepartoutOptions = useMemo(
    () => passepartoutOptions.some((option) => !!option.value),
    [passepartoutOptions],
  )
  const hasSpacerOptions = useMemo(
    () => spacerOptions.some((option) => !!option.value),
    [spacerOptions],
  )

  // const isEmptyFrame = useMemo(
  //   () => getIsEmptyFrame(combinedListingActiveProducts ?? []),
  //   [combinedListingActiveProducts],
  // )

  /**
   * Validates value for product type and deselects it, if it's invalid.
   */
  const validateProductType = useCallback(
    (type: CombinedListingProductType, size: string, value?: number) => {
      if (!value) {
        return
      }

      const options = getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.[type] ?? [],
        size,
      )
      const isValid = options.some((option) => option.value === `${value}`)

      // Deselect products that don't have a matching size
      if (!isValid) {
        clearCombinedListingProductType(type)
      }
    },
    [clearCombinedListingProductType, combinedListingConfiguration],
  )

  /**
   * Selects default glass, backboard, passepartout and spacer products.
   */
  const selectDefaults = useCallback(
    (size: string) => {
      // Select default values only once
      if (
        !combinedListingSettings ||
        !combinedListingConfiguration ||
        isDefaultSelectionLoadedRef.current
      ) {
        return
      }

      isDefaultSelectionLoadedRef.current = true

      // Stop if any product is already selected
      if (glassId || backboardId || passepartoutId || spacerId) {
        return
      }

      const defaultValues = getCombinedListingProductDefaultValues(
        combinedListingSettings,
        combinedListingConfiguration,
        size,
      )
      const defaultProductIds = [
        defaultValues.glassProductId,
        defaultValues.backboardProductId,
        defaultValues.passepartoutProductId,
        defaultValues.spacerProductId,
      ].filter(Boolean) as number[]

      defaultProductIds.forEach((productId) => {
        addCombinedListingProduct(productId)
      })
    },
    [
      addCombinedListingProduct,
      backboardId,
      combinedListingConfiguration,
      combinedListingSettings,
      glassId,
      passepartoutId,
      spacerId,
    ],
  )

  const handleFrameChange = useCallback(
    (newProductId: string) => {
      if (!newProductId) {
        return
      }

      const newProduct = combinedListingConfiguration?.frame?.find(
        (product) => product.productID === Number(newProductId),
      )

      if (!newProduct) {
        return
      }

      onMainProductChange(newProduct)
    },
    [combinedListingConfiguration, onMainProductChange],
  )

  const handleSizeChange = useCallback(
    (newSize: string) => {
      if (!newSize) {
        return
      }

      const { width, height } = getSizeDimensionsFromString(newSize)

      const newPassepartoutHoleWidth = width - PASSEPARTOUT_MIN_VALUE
      const newPassepartoutHoleHeight = height - PASSEPARTOUT_MIN_VALUE

      setPassepartoutHoleWidth(newPassepartoutHoleWidth)
      setPassepartoutHoleHeight(newPassepartoutHoleHeight)

      if (passepartoutId) {
        setCombinedListingPassepartoutHoleSize(
          `${newPassepartoutHoleWidth}x${newPassepartoutHoleHeight}`,
        )
      }

      setCombinedListingSize(newSize)

      validateProductType('glass', newSize, glassId)
      validateProductType('backboard', newSize, backboardId)
      validateProductType('passepartout', newSize, passepartoutId)
      validateProductType('spacer', newSize, spacerId)

      selectDefaults(newSize)
    },
    [
      backboardId,
      glassId,
      passepartoutId,
      selectDefaults,
      setCombinedListingSize,
      spacerId,
      validateProductType,
      setCombinedListingPassepartoutHoleSize,
    ],
  )

  const handleGlassChange = useCallback(
    (newProductId: string) => {
      isDefaultSelectionLoadedRef.current = true

      if (!newProductId) {
        clearCombinedListingProductType('glass')
        return
      }

      addCombinedListingProduct(Number(newProductId))
    },
    [addCombinedListingProduct, clearCombinedListingProductType],
  )

  const handleBackboardChange = useCallback(
    (newProductId: string) => {
      isDefaultSelectionLoadedRef.current = true

      if (!newProductId) {
        clearCombinedListingProductType('backboard')
        return
      }

      addCombinedListingProduct(Number(newProductId))
    },
    [addCombinedListingProduct, clearCombinedListingProductType],
  )

  const updateCombinedListingPassepartoutHoleSize = useCallback(() => {
    const newPassepartoutHoleWidth =
      passepartoutHoleWidth !== null
        ? passepartoutHoleWidth
        : passepartoutHoleSizeDimensions.width
    const newPassepartoutHoleHeight =
      passepartoutHoleHeight !== null
        ? passepartoutHoleHeight
        : passepartoutHoleSizeDimensions.height

    setCombinedListingPassepartoutHoleSize(
      `${newPassepartoutHoleWidth}x${newPassepartoutHoleHeight}`,
    )
  }, [
    passepartoutHoleWidth,
    passepartoutHoleHeight,
    passepartoutHoleSizeDimensions,
    setCombinedListingPassepartoutHoleSize,
  ])

  const handlePassepartoutHoleSizeChange = useCallback(() => {
    isDefaultSelectionLoadedRef.current = true
    updateCombinedListingPassepartoutHoleSize()
  }, [updateCombinedListingPassepartoutHoleSize])

  const handlePassepartoutChange = useCallback(
    (newProductId: string) => {
      isDefaultSelectionLoadedRef.current = true

      if (!newProductId) {
        clearCombinedListingProductType('passepartout')
        setCombinedListingPassepartoutHoleSize(null)

        return
      }

      updateCombinedListingPassepartoutHoleSize()
      addCombinedListingProduct(Number(newProductId))
    },
    [
      addCombinedListingProduct,
      clearCombinedListingProductType,
      setCombinedListingPassepartoutHoleSize,
      updateCombinedListingPassepartoutHoleSize,
    ],
  )

  const handleSpacerChange = useCallback(
    (newProductId: string) => {
      if (!newProductId) {
        clearCombinedListingProductType('spacer')
        return
      }

      addCombinedListingProduct(Number(newProductId))
    },
    [addCombinedListingProduct, clearCombinedListingProductType],
  )

  return (
    <div className="">
      <label className="inline-flex gap-x-1.5 items-center font-medium">
        {/* <Icon
          name="Info"
          id="frame-color-icon"
          className="text-gray-500 text-lg"
        /> */}

        {!!frameColor && (
          <>
            {strings.combinedListingFrameColor}:{' '}
            <span className="!font-normal">{frameColor}</span>
          </>
        )}
        {!frameColor && <> {strings.combinedListingFrame} </>}
      </label>

      <div className="flex flex-wrap gap-2.5 mt-3">
        {frameOptions.map((frameOption) => {
          const product = combinedListingConfiguration?.frame.find(
            (frame) => frame.productID === Number(frameOption.value),
          )

          const photo = getProductGalleryPhoto(
            product?.galleryPhotos ?? [],
            product?.listingPhotos?.[0].forOption,
            product?.listingPhotos?.[0].galleryImage,
          )

          if (photo) {
            return (
              <OptionThumbnail
                key={frameOption.value}
                photo={photo}
                active={
                  combinedListingMainProductId === Number(frameOption.value)
                }
                onClick={() => handleFrameChange(frameOption.value)}
              />
            )
          }

          return (
            <SmallGraphic
              key={frameOption.value}
              selected={
                combinedListingMainProductId === Number(frameOption.value)
              }
              backgroundColor={frameOption.color}
              backgroundImage={frameOption.imageUrl}
              onClick={() => handleFrameChange(frameOption.value)}
            />
          )
        })}
      </div>

      <div className="space-y-3 mt-6">
        <h5 className="font-medium">{strings.combinedListingConfigureFrame}</h5>

        <Accordion
          bordered
          open={accordionOpen === 'size'}
          onToggle={() => onAccordionToggle?.('size')}
          id="combined-listing-product-size"
          title={
            <>
              {strings.combinedListingSize}:{' '}
              <span className="!font-normal">
                {combinedListingActiveSize
                  ? getOptionTitle(sizeOptions, combinedListingActiveSize)
                  : (strings.combinedListingSizePlaceholder ?? '')}
              </span>
            </>
          }
        >
          <div className="flex flex-wrap gap-2.5">
            {sizeOptions.map((option) => (
              <Button
                key={option.value}
                onClick={() => handleSizeChange(option.value)}
                size="small"
                variant={
                  combinedListingActiveSize === option.value
                    ? 'filled'
                    : 'outlined'
                }
                color={
                  combinedListingActiveSize === option.value ? 'black' : 'gray'
                }
              >
                {option.title}
              </Button>
            ))}
          </div>

          {combinedListingSettings?.configuratorLink && (
            <div className="border-t pt-4 mt-5">
              <Link
                link={combinedListingSettings?.configuratorLink}
                buttonSize="small"
              >
                {strings.combinedListingConfigureCustomFrame}
              </Link>
            </div>
          )}
        </Accordion>

        {!!combinedListingActiveSize && (
          <>
            {/* {isEmptyFrame && (
              <div className="inline-block px-4 rounded-md bg-gray-700">
                {strings.combinedListingEmptyFrame}
              </div>
            )} */}

            {hasGlassOptions && (
              <Accordion
                bordered
                open={accordionOpen === 'glass'}
                onToggle={() => onAccordionToggle?.('glass')}
                id="combined-listing-product-glass"
                title={
                  <>
                    {strings.combinedListingGlass}:{' '}
                    <span className="!font-normal">
                      {getOptionTitle(glassOptions, `${glassId ?? ''}`)}
                    </span>
                  </>
                }
              >
                <div className="flex flex-col gap-2.5">
                  {glassOptions.map((option) => (
                    <OptionListItem
                      key={option.value}
                      title={option.title}
                      description={option.description}
                      price={option.price}
                      alt={option.title}
                      photo={option.imageUrl}
                      active={`${glassId ?? ''}` === option.value}
                      onClick={() => handleGlassChange(option.value)}
                    />
                  ))}
                </div>
              </Accordion>
            )}

            {hasBackboardOptions && (
              <Accordion
                bordered
                open={accordionOpen === 'backboard'}
                onToggle={() => onAccordionToggle?.('backboard')}
                id="combined-listing-product-backboard"
                title={
                  <>
                    {strings.combinedListingBackboard}:{' '}
                    <span className="!font-normal">
                      {getOptionTitle(backboardOptions, `${backboardId ?? ''}`)}
                    </span>
                  </>
                }
              >
                <div className="flex flex-col gap-2.5">
                  {backboardOptions.map((option) => (
                    <OptionListItem
                      key={option.value}
                      title={option.title}
                      description={option.description}
                      price={option.price}
                      alt={option.title}
                      photo={option.imageUrl}
                      active={`${backboardId ?? ''}` === option.value}
                      onClick={() => handleBackboardChange(option.value)}
                    />
                  ))}
                </div>
              </Accordion>
            )}

            {hasPassepartoutOptions && (
              <Accordion
                bordered
                open={accordionOpen === 'passepartout'}
                onToggle={() => onAccordionToggle?.('passepartout')}
                id="combined-listing-product-backboard"
                innerClassName="space-y-5"
                title={
                  <>
                    {strings.combinedListingPassepartout}:{' '}
                    <span className="!font-normal">
                      {getOptionTitle(
                        passepartoutOptions,
                        `${passepartoutId ?? ''}`,
                      )}
                    </span>
                  </>
                }
              >
                <div className="space-y-3">
                  {strings.combinedListingPassepartoutDescription && (
                    <SimplePortableText
                      content={strings.combinedListingPassepartoutDescription}
                      className="[&>*]:text-sm [&>*]:leading-[1.4] [&>*]:tracking-[2%]"
                    />
                  )}

                  <div className="flex flex-col gap-2.5">
                    {passepartoutOptions.map((option) => (
                      <OptionListItem
                        key={option.value}
                        title={option.title}
                        description={option.description}
                        price={option.price}
                        alt={option.title}
                        photo={option.imageUrl}
                        active={`${passepartoutId ?? ''}` === option.value}
                        onClick={() => handlePassepartoutChange(option.value)}
                      />
                    ))}
                  </div>
                </div>

                {!!passepartoutId && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium">
                      {strings.combinedListingPassepartoutHoleSize}
                    </label>

                    <div className="space-y-2">
                      <RangeSlider
                        label="Width (cm)"
                        value={
                          passepartoutHoleWidth ??
                          passepartoutHoleSizeDimensions.width
                        }
                        setValue={setPassepartoutHoleWidth}
                        onMouseUp={handlePassepartoutHoleSizeChange}
                        onTouchEnd={handlePassepartoutHoleSizeChange}
                        onBlur={handlePassepartoutHoleSizeChange}
                        min={PASSEPARTOUT_MIN_VALUE}
                        max={sizeDimensions.width - PASSEPARTOUT_MIN_VALUE}
                      />

                      <RangeSlider
                        label="Height (cm)"
                        value={
                          passepartoutHoleHeight ??
                          passepartoutHoleSizeDimensions.height
                        }
                        setValue={setPassepartoutHoleHeight}
                        onMouseUp={handlePassepartoutHoleSizeChange}
                        onTouchEnd={handlePassepartoutHoleSizeChange}
                        onBlur={handlePassepartoutHoleSizeChange}
                        min={PASSEPARTOUT_MIN_VALUE}
                        max={sizeDimensions.height - PASSEPARTOUT_MIN_VALUE}
                      />
                    </div>
                  </div>
                )}
              </Accordion>
            )}

            {hasSpacerOptions && (
              <Accordion
                bordered
                open={accordionOpen === 'spacer'}
                onToggle={() => onAccordionToggle?.('spacer')}
                id="combined-listing-product-spacer"
                innerClassName="space-y-5"
                title={
                  <>
                    {strings.combinedListingSpacer}:{' '}
                    <span className="!font-normal">
                      {getOptionTitle(spacerOptions, `${spacerId ?? ''}`)}
                    </span>
                  </>
                }
              >
                <div className="flex flex-col gap-2.5">
                  {spacerOptions.map((option) => (
                    <OptionListItem
                      key={option.value}
                      title={option.title}
                      description={option.description}
                      price={option.price}
                      alt={option.title}
                      photo={option.imageUrl}
                      active={`${passepartoutId ?? ''}` === option.value}
                      onClick={() => handleSpacerChange(option.value)}
                    />
                  ))}
                </div>
              </Accordion>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CombinedListingProductSelect
